import { DeviceType } from '../constants/device'
import type { ImageInfo, ValidationWarning, DeviceResolution } from '../types/device'

export interface DeviceSpecification {
  type: DeviceType
  name: string
  displayName: string
  icon: string
  aspectRatios: AspectRatio[]
  recommendedResolutions: DeviceResolution[]
  description: string
  mockupFrames: MockupFrame[]
  validationRules: ValidationRules
}

export interface AspectRatio {
  ratio: string
  description: string
  isCommon: boolean
  width: number
  height: number
}

export interface MockupFrame {
  name: string
  width: number
  height: number
  frameUrl: string
  screenBounds: {
    x: number
    y: number
    width: number
    height: number
  }
}

export interface ValidationRules {
  minWidth: number
  minHeight: number
  maxFileSize: number
  supportedFormats: string[]
  optimalAspectRatios: string[]
}

class DeviceRecommendationService {
  private readonly DEVICE_SPECIFICATIONS: Map<DeviceType, DeviceSpecification> = new Map([
    [DeviceType.MOBILE_PHONE, {
      type: DeviceType.MOBILE_PHONE,
      name: 'mobile_phone',
      displayName: 'Mobile Phone',
      icon: '📱',
      aspectRatios: [
        { ratio: '9:16', description: 'Standard mobile portrait', isCommon: true, width: 9, height: 16 },
        { ratio: '9:19.5', description: 'Modern mobile portrait', isCommon: true, width: 9, height: 19.5 },
        { ratio: '9:20', description: 'iPhone X+ portrait', isCommon: true, width: 9, height: 20 }
      ],
      recommendedResolutions: [
        { width: 1080, height: 1920, label: 'Full HD Mobile', isOptimal: true },
        { width: 1125, height: 2436, label: 'iPhone X/XS', isOptimal: true },
        { width: 1170, height: 2532, label: 'iPhone 12/13/14', isOptimal: true },
        { width: 1284, height: 2778, label: 'iPhone 12/13/14 Pro Max', isOptimal: false },
        { width: 720, height: 1280, label: 'HD Mobile', isOptimal: false }
      ],
      description: 'Optimized for mobile phone screens in portrait orientation',
      mockupFrames: [
        {
          name: 'iPhone 14 Pro',
          width: 393,
          height: 852,
          frameUrl: '/mockups/iphone-14-pro.png',
          screenBounds: { x: 20, y: 60, width: 353, height: 732 }
        }
      ],
      validationRules: {
        minWidth: 720,
        minHeight: 1280,
        maxFileSize: 10 * 1024 * 1024, // 10MB
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        optimalAspectRatios: ['9:16', '9:19.5', '9:20']
      }
    }],

    [DeviceType.TABLET, {
      type: DeviceType.TABLET,
      name: 'tablet',
      displayName: 'Tablet',
      icon: '📱',
      aspectRatios: [
        { ratio: '4:3', description: 'iPad standard', isCommon: true, width: 4, height: 3 },
        { ratio: '16:10', description: 'Android tablet', isCommon: true, width: 16, height: 10 },
        { ratio: '3:2', description: 'Surface tablet', isCommon: false, width: 3, height: 2 }
      ],
      recommendedResolutions: [
        { width: 2048, height: 2732, label: 'iPad Pro 12.9"', isOptimal: true },
        { width: 1668, height: 2388, label: 'iPad Pro 11"', isOptimal: true },
        { width: 1536, height: 2048, label: 'iPad Air/Mini', isOptimal: true },
        { width: 1920, height: 1200, label: 'Android Tablet', isOptimal: false },
        { width: 1024, height: 768, label: 'Standard Tablet', isOptimal: false }
      ],
      description: 'Optimized for tablet screens in both portrait and landscape',
      mockupFrames: [
        {
          name: 'iPad Pro',
          width: 834,
          height: 1194,
          frameUrl: '/mockups/ipad-pro.png',
          screenBounds: { x: 40, y: 60, width: 754, height: 1074 }
        }
      ],
      validationRules: {
        minWidth: 1024,
        minHeight: 768,
        maxFileSize: 15 * 1024 * 1024, // 15MB
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        optimalAspectRatios: ['4:3', '16:10', '3:2']
      }
    }],

    [DeviceType.DESKTOP, {
      type: DeviceType.DESKTOP,
      name: 'desktop',
      displayName: 'Desktop',
      icon: '🖥️',
      aspectRatios: [
        { ratio: '16:9', description: 'Standard widescreen', isCommon: true, width: 16, height: 9 },
        { ratio: '16:10', description: 'Widescreen alternative', isCommon: true, width: 16, height: 10 },
        { ratio: '4:3', description: 'Traditional desktop', isCommon: false, width: 4, height: 3 }
      ],
      recommendedResolutions: [
        { width: 1920, height: 1080, label: 'Full HD', isOptimal: true },
        { width: 2560, height: 1440, label: '1440p QHD', isOptimal: true },
        { width: 3840, height: 2160, label: '4K UHD', isOptimal: true },
        { width: 1366, height: 768, label: 'HD', isOptimal: false },
        { width: 1680, height: 1050, label: 'WSXGA+', isOptimal: false }
      ],
      description: 'Optimized for desktop monitors and laptop screens',
      mockupFrames: [
        {
          name: 'MacBook Pro',
          width: 1440,
          height: 900,
          frameUrl: '/mockups/macbook-pro.png',
          screenBounds: { x: 120, y: 80, width: 1200, height: 740 }
        }
      ],
      validationRules: {
        minWidth: 1366,
        minHeight: 768,
        maxFileSize: 20 * 1024 * 1024, // 20MB
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        optimalAspectRatios: ['16:9', '16:10']
      }
    }],

    [DeviceType.IPHONE, {
      type: DeviceType.IPHONE,
      name: 'iphone',
      displayName: 'iPhone',
      icon: '📱',
      aspectRatios: [
        { ratio: '9:19.5', description: 'iPhone 12/13/14 portrait', isCommon: true, width: 9, height: 19.5 },
        { ratio: '9:16', description: 'Standard mobile portrait', isCommon: true, width: 9, height: 16 }
      ],
      recommendedResolutions: [
        { width: 1170, height: 2532, label: 'iPhone 12/13/14', isOptimal: true },
        { width: 1125, height: 2436, label: 'iPhone X/XS/11 Pro', isOptimal: true },
        { width: 828, height: 1792, label: 'iPhone XR/11', isOptimal: false }
      ],
      description: 'Optimized for iPhone devices with modern aspect ratios',
      mockupFrames: [
        {
          name: 'iPhone 14',
          width: 390,
          height: 844,
          frameUrl: '/mockups/iphone-14.png',
          screenBounds: { x: 20, y: 60, width: 350, height: 724 }
        }
      ],
      validationRules: {
        minWidth: 828,
        minHeight: 1792,
        maxFileSize: 10 * 1024 * 1024,
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp', 'heic'],
        optimalAspectRatios: ['9:19.5', '9:16']
      }
    }],

    [DeviceType.IPHONE_PRO, {
      type: DeviceType.IPHONE_PRO,
      name: 'iphone_pro',
      displayName: 'iPhone Pro',
      icon: '📱',
      aspectRatios: [
        { ratio: '9:19.5', description: 'iPhone Pro portrait', isCommon: true, width: 9, height: 19.5 },
        { ratio: '9:20', description: 'iPhone Pro Max portrait', isCommon: true, width: 9, height: 20 }
      ],
      recommendedResolutions: [
        { width: 1179, height: 2556, label: 'iPhone 14/15 Pro', isOptimal: true },
        { width: 1284, height: 2778, label: 'iPhone 14/15 Pro Max', isOptimal: true },
        { width: 1125, height: 2436, label: 'iPhone X/XS/11 Pro', isOptimal: false }
      ],
      description: 'Optimized for iPhone Pro models with ProMotion displays',
      mockupFrames: [
        {
          name: 'iPhone 14 Pro',
          width: 393,
          height: 852,
          frameUrl: '/mockups/iphone-14-pro.png',
          screenBounds: { x: 20, y: 60, width: 353, height: 732 }
        }
      ],
      validationRules: {
        minWidth: 1125,
        minHeight: 2436,
        maxFileSize: 12 * 1024 * 1024,
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp', 'heic'],
        optimalAspectRatios: ['9:19.5', '9:20']
      }
    }],

    [DeviceType.ANDROID, {
      type: DeviceType.ANDROID,
      name: 'android',
      displayName: 'Android',
      icon: '📱',
      aspectRatios: [
        { ratio: '9:20', description: 'Modern Android portrait', isCommon: true, width: 9, height: 20 },
        { ratio: '9:18', description: 'Android 18:9 portrait', isCommon: true, width: 9, height: 18 },
        { ratio: '9:16', description: 'Standard mobile portrait', isCommon: false, width: 9, height: 16 }
      ],
      recommendedResolutions: [
        { width: 1080, height: 2400, label: 'FHD+ Android', isOptimal: true },
        { width: 1440, height: 3200, label: 'QHD+ Android', isOptimal: true },
        { width: 1080, height: 2340, label: 'FHD+ 19.5:9', isOptimal: false },
        { width: 720, height: 1600, label: 'HD+ Android', isOptimal: false }
      ],
      description: 'Optimized for Android devices with various aspect ratios',
      mockupFrames: [
        {
          name: 'Android Phone',
          width: 360,
          height: 800,
          frameUrl: '/mockups/android-phone.png',
          screenBounds: { x: 20, y: 60, width: 320, height: 680 }
        }
      ],
      validationRules: {
        minWidth: 720,
        minHeight: 1280,
        maxFileSize: 10 * 1024 * 1024,
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        optimalAspectRatios: ['9:20', '9:18']
      }
    }],

    [DeviceType.LAPTOP, {
      type: DeviceType.LAPTOP,
      name: 'laptop',
      displayName: 'Laptop',
      icon: '💻',
      aspectRatios: [
        { ratio: '16:10', description: 'MacBook aspect ratio', isCommon: true, width: 16, height: 10 },
        { ratio: '16:9', description: 'Standard laptop', isCommon: true, width: 16, height: 9 },
        { ratio: '3:2', description: 'Surface laptop', isCommon: false, width: 3, height: 2 }
      ],
      recommendedResolutions: [
        { width: 1920, height: 1200, label: 'WUXGA 16:10', isOptimal: true },
        { width: 2560, height: 1600, label: 'WQXGA 16:10', isOptimal: true },
        { width: 1920, height: 1080, label: 'Full HD 16:9', isOptimal: false },
        { width: 1366, height: 768, label: 'HD 16:9', isOptimal: false }
      ],
      description: 'Optimized for laptop screens with 16:10 or 16:9 aspect ratios',
      mockupFrames: [
        {
          name: 'MacBook Pro',
          width: 1440,
          height: 900,
          frameUrl: '/mockups/macbook-pro.png',
          screenBounds: { x: 120, y: 80, width: 1200, height: 740 }
        }
      ],
      validationRules: {
        minWidth: 1366,
        minHeight: 768,
        maxFileSize: 20 * 1024 * 1024,
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        optimalAspectRatios: ['16:10', '16:9']
      }
    }],

    [DeviceType.TV, {
      type: DeviceType.TV,
      name: 'tv',
      displayName: 'Smart TV',
      icon: '📺',
      aspectRatios: [
        { ratio: '16:9', description: 'Standard TV aspect ratio', isCommon: true, width: 16, height: 9 },
        { ratio: '21:9', description: 'Ultrawide TV', isCommon: false, width: 21, height: 9 }
      ],
      recommendedResolutions: [
        { width: 3840, height: 2160, label: '4K UHD', isOptimal: true },
        { width: 1920, height: 1080, label: 'Full HD', isOptimal: false },
        { width: 7680, height: 4320, label: '8K UHD', isOptimal: true }
      ],
      description: 'Optimized for smart TV displays and large screens',
      mockupFrames: [
        {
          name: 'Smart TV',
          width: 1920,
          height: 1080,
          frameUrl: '/mockups/smart-tv.png',
          screenBounds: { x: 60, y: 60, width: 1800, height: 960 }
        }
      ],
      validationRules: {
        minWidth: 1920,
        minHeight: 1080,
        maxFileSize: 30 * 1024 * 1024,
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        optimalAspectRatios: ['16:9']
      }
    }],

    [DeviceType.WATCH, {
      type: DeviceType.WATCH,
      name: 'watch',
      displayName: 'Smartwatch',
      icon: '⌚',
      aspectRatios: [
        { ratio: '1:1', description: 'Square watch face', isCommon: true, width: 1, height: 1 },
        { ratio: '4:5', description: 'Rectangular watch', isCommon: false, width: 4, height: 5 }
      ],
      recommendedResolutions: [
        { width: 396, height: 396, label: 'Apple Watch 44mm', isOptimal: true },
        { width: 368, height: 448, label: 'Apple Watch Ultra', isOptimal: true },
        { width: 324, height: 394, label: 'Apple Watch 40mm', isOptimal: false },
        { width: 450, height: 450, label: 'Galaxy Watch', isOptimal: false }
      ],
      description: 'Optimized for smartwatch displays with square or rectangular faces',
      mockupFrames: [
        {
          name: 'Apple Watch',
          width: 184,
          height: 224,
          frameUrl: '/mockups/apple-watch.png',
          screenBounds: { x: 20, y: 40, width: 144, height: 144 }
        }
      ],
      validationRules: {
        minWidth: 300,
        minHeight: 300,
        maxFileSize: 5 * 1024 * 1024,
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        optimalAspectRatios: ['1:1']
      }
    }],

    [DeviceType.ULTRAWIDE, {
      type: DeviceType.ULTRAWIDE,
      name: 'ultrawide',
      displayName: 'Ultrawide Monitor',
      icon: '🖥️',
      aspectRatios: [
        { ratio: '21:9', description: 'Standard ultrawide', isCommon: true, width: 21, height: 9 },
        { ratio: '32:9', description: 'Super ultrawide', isCommon: true, width: 32, height: 9 },
        { ratio: '18:9', description: 'Compact ultrawide', isCommon: false, width: 18, height: 9 }
      ],
      recommendedResolutions: [
        { width: 3440, height: 1440, label: 'UWQHD', isOptimal: true },
        { width: 5120, height: 1440, label: 'DQHD', isOptimal: true },
        { width: 2560, height: 1080, label: 'UW-UXGA', isOptimal: false },
        { width: 3840, height: 1600, label: 'UW4K', isOptimal: true }
      ],
      description: 'Optimized for ultrawide monitors with 21:9 or 32:9 aspect ratios',
      mockupFrames: [
        {
          name: 'Ultrawide Monitor',
          width: 1720,
          height: 720,
          frameUrl: '/mockups/ultrawide-monitor.png',
          screenBounds: { x: 60, y: 60, width: 1600, height: 600 }
        }
      ],
      validationRules: {
        minWidth: 2560,
        minHeight: 1080,
        maxFileSize: 25 * 1024 * 1024, // 25MB
        supportedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        optimalAspectRatios: ['21:9', '32:9']
      }
    }]
  ])

  /**
   * Get all device specifications
   */
  getDeviceSpecifications(): DeviceSpecification[] {
    return Array.from(this.DEVICE_SPECIFICATIONS.values())
  }

  /**
   * Get specification for a specific device
   */
  getDeviceSpecification(device: DeviceType): DeviceSpecification | null {
    return this.DEVICE_SPECIFICATIONS.get(device) || null
  }

  /**
   * Get recommended resolutions for a device
   */
  getRecommendedResolutions(device: DeviceType): DeviceResolution[] {
    const spec = this.DEVICE_SPECIFICATIONS.get(device)
    return spec?.recommendedResolutions || []
  }

  /**
   * Get optimal aspect ratio for a device
   */
  getOptimalAspectRatio(device: DeviceType): AspectRatio | null {
    const spec = this.DEVICE_SPECIFICATIONS.get(device)
    return spec?.aspectRatios.find(ar => ar.isCommon) || spec?.aspectRatios[0] || null
  }

  /**
   * Validate image for specific device
   */
  validateImageForDevice(imageInfo: ImageInfo, device: DeviceType): ValidationWarning[] {
    const warnings: ValidationWarning[] = []
    const spec = this.DEVICE_SPECIFICATIONS.get(device)

    if (!spec) {
      warnings.push({
        type: 'format',
        message: `Unknown device type: ${device}`,
        severity: 'error'
      })
      return warnings
    }

    const { width, height, format } = imageInfo
    const { validationRules } = spec

    // Check minimum dimensions
    if (width < validationRules.minWidth || height < validationRules.minHeight) {
      warnings.push({
        type: 'dimension',
        message: `Image resolution ${width}×${height} is below recommended minimum ${validationRules.minWidth}×${validationRules.minHeight}`,
        severity: 'warning',
        suggestion: `Consider using a higher resolution image for better quality on ${spec.displayName}`
      })
    }

    // Check aspect ratio
    const imageAspectRatio = width / height
    const isOptimalAspectRatio = spec.aspectRatios.some(ar => {
      const targetRatio = ar.width / ar.height
      return Math.abs(imageAspectRatio - targetRatio) < 0.1
    })

    if (!isOptimalAspectRatio) {
      const optimalRatio = this.getOptimalAspectRatio(device)
      warnings.push({
        type: 'aspect_ratio',
        message: `Image aspect ratio ${imageInfo.aspectRatio} may not be optimal for ${spec.displayName}`,
        severity: 'warning',
        suggestion: optimalRatio ? `Consider using ${optimalRatio.ratio} aspect ratio for best results` : undefined
      })
    }

    // Check format
    if (format && !validationRules.supportedFormats.includes(format.toLowerCase())) {
      warnings.push({
        type: 'format',
        message: `Image format ${format.toUpperCase()} may not be optimal for ${spec.displayName}`,
        severity: 'warning',
        suggestion: 'Consider using JPG, PNG, or WebP format'
      })
    }

    // Check if image is too small for device
    const optimalResolutions = spec.recommendedResolutions.filter(r => r.isOptimal)
    if (optimalResolutions.length > 0) {
      const minOptimalWidth = Math.min(...optimalResolutions.map(r => r.width))
      const minOptimalHeight = Math.min(...optimalResolutions.map(r => r.height))

      if (width < minOptimalWidth * 0.8 || height < minOptimalHeight * 0.8) {
        warnings.push({
          type: 'quality',
          message: `Image may appear pixelated on ${spec.displayName}`,
          severity: 'warning',
          suggestion: `Consider using at least ${minOptimalWidth}×${minOptimalHeight} resolution`
        })
      }
    }

    return warnings
  }

  /**
   * Calculate quality score for image on specific device
   */
  calculateQualityScore(imageInfo: ImageInfo, device: DeviceType): number {
    const spec = this.DEVICE_SPECIFICATIONS.get(device)
    if (!spec) return 0

    let score = 100
    const { width, height, format } = imageInfo
    const { validationRules } = spec

    // Resolution score (40% of total)
    const optimalResolutions = spec.recommendedResolutions.filter(r => r.isOptimal)
    if (optimalResolutions.length > 0) {
      const avgOptimalWidth = optimalResolutions.reduce((sum, r) => sum + r.width, 0) / optimalResolutions.length
      const avgOptimalHeight = optimalResolutions.reduce((sum, r) => sum + r.height, 0) / optimalResolutions.length

      const widthRatio = Math.min(width / avgOptimalWidth, 1)
      const heightRatio = Math.min(height / avgOptimalHeight, 1)
      const resolutionScore = (widthRatio + heightRatio) / 2

      score = score * 0.6 + (resolutionScore * 40)
    }

    // Aspect ratio score (30% of total)
    const imageAspectRatio = width / height
    const bestAspectRatio = spec.aspectRatios.find(ar => ar.isCommon) || spec.aspectRatios[0]
    if (bestAspectRatio) {
      const targetRatio = bestAspectRatio.width / bestAspectRatio.height
      const ratioDifference = Math.abs(imageAspectRatio - targetRatio) / targetRatio
      const aspectScore = Math.max(0, 1 - ratioDifference * 2)

      score = score * 0.7 + (aspectScore * 30)
    }

    // Format score (10% of total)
    if (format) {
      const formatScore = validationRules.supportedFormats.includes(format.toLowerCase()) ? 1 : 0.7
      score = score * 0.9 + (formatScore * 10)
    }

    // Apply penalties for validation warnings
    const warnings = this.validateImageForDevice(imageInfo, device)
    const errorPenalty = warnings.filter(w => w.severity === 'error').length * 25
    const warningPenalty = warnings.filter(w => w.severity === 'warning').length * 10

    score = Math.max(0, score - errorPenalty - warningPenalty)

    return Math.round(score)
  }

  /**
   * Get device-specific recommendations
   */
  getDeviceRecommendations(device: DeviceType): {
    optimalResolutions: DeviceResolution[]
    aspectRatios: AspectRatio[]
    tips: string[]
  } {
    const spec = this.DEVICE_SPECIFICATIONS.get(device)
    if (!spec) {
      return { optimalResolutions: [], aspectRatios: [], tips: [] }
    }

    const tips = [
      `Use ${spec.aspectRatios.filter(ar => ar.isCommon).map(ar => ar.ratio).join(' or ')} aspect ratio for best results`,
      `Minimum resolution: ${spec.validationRules.minWidth}×${spec.validationRules.minHeight}`,
      `Supported formats: ${spec.validationRules.supportedFormats.map(f => f.toUpperCase()).join(', ')}`,
      `Maximum file size: ${Math.round(spec.validationRules.maxFileSize / (1024 * 1024))}MB`
    ]

    return {
      optimalResolutions: spec.recommendedResolutions.filter(r => r.isOptimal),
      aspectRatios: spec.aspectRatios,
      tips
    }
  }

  /**
   * Suggest optimal device for image
   */
  suggestOptimalDevice(imageInfo: ImageInfo): {
    device: DeviceType
    confidence: number
    reason: string
  } {
    const { width, height } = imageInfo
    const aspectRatio = width / height

    let bestDevice = DeviceType.DESKTOP
    let bestScore = 0
    let reason = ''

    for (const [device] of this.DEVICE_SPECIFICATIONS) {
      const score = this.calculateQualityScore(imageInfo, device)

      if (score > bestScore) {
        bestScore = score
        bestDevice = device

        // Generate reason based on aspect ratio and resolution
        if (aspectRatio < 1) {
          reason = 'Portrait orientation suggests mobile device'
        } else if (aspectRatio > 2) {
          reason = 'Ultra-wide aspect ratio suggests ultrawide monitor'
        } else if (width >= 2560) {
          reason = 'High resolution suggests desktop/ultrawide display'
        } else if (aspectRatio > 1.5) {
          reason = 'Landscape orientation suggests desktop/tablet'
        } else {
          reason = 'Aspect ratio and resolution suggest tablet device'
        }
      }
    }

    return {
      device: bestDevice,
      confidence: bestScore,
      reason
    }
  }

  /**
   * Validate device pack consistency
   */
  validateDevicePackConsistency(variations: Map<DeviceType, any>): ValidationWarning[] {
    const warnings: ValidationWarning[] = []

    // Check for consistent themes across devices
    const devices = Array.from(variations.keys())
    if (devices.length > 1) {
      // Add consistency validation logic here
      // For now, return empty array as this is a placeholder
    }

    return warnings
  }

  /**
   * Get optimization recommendations for image and device
   */
  getOptimizationRecommendations(imageInfo: ImageInfo, device: DeviceType): Array<{
    title: string
    description: string
    impact: string
  }> {
    const recommendations: Array<{
      title: string
      description: string
      impact: string
    }> = []

    const spec = this.getDeviceSpecification(device)
    if (!spec) return recommendations

    // Add optimization recommendations based on image and device
    const qualityScore = this.calculateQualityScore(imageInfo, device)

    if (qualityScore < 80) {
      recommendations.push({
        title: 'Improve image quality',
        description: `Current quality score is ${qualityScore}%. Consider using a higher resolution image.`,
        impact: 'high'
      })
    }

    return recommendations
  }
}

export const deviceRecommendationService = new DeviceRecommendationService()